import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type {
  Workspace,
  Capability,
  CapabilityNode,
  Edge,
  Collection,
  WorkspaceId,
  CapabilityId,
  NodeId,
  EdgeId,
} from '@/types';

// ─── State shape ──────────────────────────────────────────────────────────────

interface WorkspaceState {
  workspaces: Workspace[];
  activeWorkspaceId: WorkspaceId | null;
  capabilities: Record<CapabilityId, Capability>;
  nodes: Record<NodeId, CapabilityNode>;
  edges: Record<EdgeId, Edge>;
  collections: Record<string, Collection>;
  isLoadingCapabilities: boolean;
  isLoadingGraph: boolean;
  lastSyncedAt: string | null;
}

interface WorkspaceActions {
  setWorkspaces: (workspaces: Workspace[]) => void;
  setActiveWorkspace: (id: WorkspaceId | null) => void;
  addWorkspace: (workspace: Workspace) => void;
  updateWorkspace: (id: WorkspaceId, updates: Partial<Workspace>) => void;
  removeWorkspace: (id: WorkspaceId) => void;

  setCapabilities: (capabilities: Capability[]) => void;
  upsertCapability: (capability: Capability) => void;
  removeCapability: (id: CapabilityId) => void;

  setNodes: (nodes: CapabilityNode[]) => void;
  upsertNode: (node: CapabilityNode) => void;
  removeNode: (id: NodeId) => void;

  setEdges: (edges: Edge[]) => void;
  upsertEdge: (edge: Edge) => void;
  removeEdge: (id: EdgeId) => void;

  setCollections: (collections: Collection[]) => void;
  upsertCollection: (collection: Collection) => void;
  removeCollection: (id: string) => void;

  setIsLoadingCapabilities: (loading: boolean) => void;
  setIsLoadingGraph: (loading: boolean) => void;
  setLastSyncedAt: (ts: string) => void;

  clearWorkspaceData: () => void;
}

type WorkspaceStore = WorkspaceState & WorkspaceActions;

// ─── Store ────────────────────────────────────────────────────────────────────

export const useWorkspaceStore = create<WorkspaceStore>()(
  persist(
    (set) => ({
      // State
      workspaces: [],
      activeWorkspaceId: null,
      capabilities: {},
      nodes: {},
      edges: {},
      collections: {},
      isLoadingCapabilities: false,
      isLoadingGraph: false,
      lastSyncedAt: null,

      // Workspace actions
      setWorkspaces: (workspaces) => set({ workspaces }),

      setActiveWorkspace: (id) =>
        set({ activeWorkspaceId: id, capabilities: {}, nodes: {}, edges: {}, collections: {} }),

      addWorkspace: (workspace) =>
        set((s) => ({ workspaces: [...s.workspaces, workspace] })),

      updateWorkspace: (id, updates) =>
        set((s) => ({
          workspaces: s.workspaces.map((w) => (w.id === id ? { ...w, ...updates } : w)),
        })),

      removeWorkspace: (id) =>
        set((s) => ({
          workspaces: s.workspaces.filter((w) => w.id !== id),
          activeWorkspaceId: s.activeWorkspaceId === id ? null : s.activeWorkspaceId,
        })),

      // Capability actions
      setCapabilities: (capabilities) =>
        set({
          capabilities: Object.fromEntries(capabilities.map((c) => [c.id, c])),
        }),

      upsertCapability: (capability) =>
        set((s) => ({
          capabilities: { ...s.capabilities, [capability.id]: capability },
        })),

      removeCapability: (id) =>
        set((s) => {
          const next = { ...s.capabilities };
          delete next[id];
          return { capabilities: next };
        }),

      // Node actions
      setNodes: (nodes) =>
        set({ nodes: Object.fromEntries(nodes.map((n) => [n.id, n])) }),

      upsertNode: (node) =>
        set((s) => ({ nodes: { ...s.nodes, [node.id]: node } })),

      removeNode: (id) =>
        set((s) => {
          const next = { ...s.nodes };
          delete next[id];
          return { nodes: next };
        }),

      // Edge actions
      setEdges: (edges) =>
        set({ edges: Object.fromEntries(edges.map((e) => [e.id, e])) }),

      upsertEdge: (edge) =>
        set((s) => ({ edges: { ...s.edges, [edge.id]: edge } })),

      removeEdge: (id) =>
        set((s) => {
          const next = { ...s.edges };
          delete next[id];
          return { edges: next };
        }),

      // Collection actions
      setCollections: (collections) =>
        set({ collections: Object.fromEntries(collections.map((c) => [c.id, c])) }),

      upsertCollection: (collection) =>
        set((s) => ({ collections: { ...s.collections, [collection.id]: collection } })),

      removeCollection: (id) =>
        set((s) => {
          const next = { ...s.collections };
          delete next[id];
          return { collections: next };
        }),

      // Loading states
      setIsLoadingCapabilities: (loading) => set({ isLoadingCapabilities: loading }),
      setIsLoadingGraph: (loading) => set({ isLoadingGraph: loading }),
      setLastSyncedAt: (ts) => set({ lastSyncedAt: ts }),

      // Reset
      clearWorkspaceData: () =>
        set({ capabilities: {}, nodes: {}, edges: {}, collections: {} }),
    }),
    {
      name: 'capOS-workspace',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        workspaces: state.workspaces,
        activeWorkspaceId: state.activeWorkspaceId,
        lastSyncedAt: state.lastSyncedAt,
      }),
    }
  )
);

// ─── Selectors ────────────────────────────────────────────────────────────────

export const selectActiveWorkspace = (state: WorkspaceStore) =>
  state.workspaces.find((w) => w.id === state.activeWorkspaceId) ?? null;

export const selectCapabilitiesList = (state: WorkspaceStore) =>
  Object.values(state.capabilities).sort(
    (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
  );

export const selectNodesList = (state: WorkspaceStore) => Object.values(state.nodes);

export const selectEdgesList = (state: WorkspaceStore) => Object.values(state.edges);

export const selectCollectionsList = (state: WorkspaceStore) =>
  Object.values(state.collections).sort(
    (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
  );
